import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Check, AlertTriangle, ArrowUpRight, ArrowDownRight, RotateCcw, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtDate = (unix) => unix ? new Date(unix * 1000).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";
const daysLeft = (unix) => unix ? Math.max(0, Math.ceil((unix * 1000 - Date.now()) / 86400000)) : 0;

const PLAN_META = {
  basic: { name: "Basic", price: "4.99", color: "#FF3B30" },
  pro: { name: "Pro", price: "14.99", color: "#007AFF" },
};

export default function Account() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => { refreshUser(); /* eslint-disable-next-line */ }, []);

  const meta = PLAN_META[user?.plan] || { name: "—", price: "0", color: "#71717A" };
  const trialing = user?.sub_status === "trialing";
  const canceling = user?.cancel_at_period_end;

  const run = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
      await refreshUser();
      toast.success("Subscription updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(null); setConfirmCancel(false); }
  };

  const switchTo = (lookup_key) => run("switch", () => api.post("/subscription/switch", { lookup_key }));
  const cancel = () => run("cancel", () => api.post("/subscription/cancel"));
  const resume = () => run("resume", () => api.post("/subscription/resume"));

  const isComp = user?.role === "admin" && !user?.current_period_end;

  return (
    <div className="p-6 md:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF3B30] mb-2">Account</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Subscription</h1>
      </div>

      {/* Current plan card */}
      <div className="p-8 bg-[#121212] border border-white/10 mb-6" data-testid="subscription-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display font-black text-4xl uppercase tracking-tight" style={{ color: meta.color }}>{meta.name}</span>
              <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 border" style={{ color: meta.color, borderColor: meta.color + "55" }} data-testid="sub-status-badge">
                {trialing ? "Trial" : user?.sub_status}
              </span>
            </div>
            <div className="text-white/50 mt-1">${meta.price}/month</div>
          </div>
          <CreditCard style={{ color: meta.color }} size={32} />
        </div>

        {isComp && (
          <div className="mt-6 text-sm text-white/60 border-t border-white/10 pt-4">Complimentary admin access — no billing.</div>
        )}

        {!isComp && (
          <div className="mt-6 border-t border-white/10 pt-4 space-y-2">
            {trialing && (
              <div className="flex items-center gap-2 text-[#34C759] font-semibold" data-testid="account-trial-info">
                <Check size={18} /> {daysLeft(user?.trial_end)} days left in your free trial
              </div>
            )}
            <div className="text-sm text-white/50" data-testid="renewal-info">
              {canceling
                ? `Access ends on ${fmtDate(user?.current_period_end)}`
                : trialing
                  ? `First charge on ${fmtDate(user?.trial_end || user?.current_period_end)}`
                  : `Renews on ${fmtDate(user?.current_period_end)}`}
            </div>
          </div>
        )}
      </div>

      {!isComp && (
        <>
          {canceling && (
            <div className="p-5 border border-[#FF9500]/40 bg-[#FF9500]/10 flex items-center gap-4 mb-6" data-testid="canceling-banner">
              <AlertTriangle className="text-[#FF9500] shrink-0" size={24} />
              <div className="flex-1">
                <div className="font-semibold">Your subscription is set to cancel</div>
                <div className="text-white/60 text-sm">You'll keep access until {fmtDate(user?.current_period_end)}.</div>
              </div>
              <button data-testid="resume-subscription" onClick={resume} disabled={busy} className="bg-[#FF9500] hover:bg-[#ffa733] text-black font-bold uppercase tracking-wide text-sm px-4 py-2.5 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5">
                <RotateCcw size={16} /> {busy === "resume" ? "..." : "Resume"}
              </button>
            </div>
          )}

          {/* Change plan */}
          <div className="p-6 bg-[#121212] border border-white/10 mb-6">
            <h3 className="font-display font-bold text-lg uppercase tracking-tight mb-4">Change Plan</h3>
            {user?.plan === "basic" ? (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-[#007AFF]">Upgrade to Pro</div>
                  <div className="text-white/50 text-sm">Unlock the AI Physical Coach — $14.99/mo.</div>
                </div>
                <button data-testid="switch-to-pro" onClick={() => switchTo("pro_monthly")} disabled={busy} className="bg-[#007AFF] hover:bg-[#1a8cff] text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5">
                  <ArrowUpRight size={16} /> {busy === "switch" ? "..." : "Upgrade"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-white/80">Switch to Basic</div>
                  <div className="text-white/50 text-sm">Keep tracking, drop the AI coach — $4.99/mo.</div>
                </div>
                <button data-testid="switch-to-basic" onClick={() => switchTo("basic_monthly")} disabled={busy} className="border border-white/20 hover:border-white/50 text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 flex items-center gap-2 transition-colors">
                  <ArrowDownRight size={16} /> {busy === "switch" ? "..." : "Switch"}
                </button>
              </div>
            )}
          </div>

          {/* Cancel */}
          {!canceling && (
            <div className="p-6 border border-white/10">
              {!confirmCancel ? (
                <button data-testid="cancel-subscription" onClick={() => setConfirmCancel(true)} className="text-white/40 hover:text-[#FF3B30] text-sm uppercase tracking-wide font-semibold transition-colors">
                  Cancel subscription
                </button>
              ) : (
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="text-sm text-white/70">Cancel your subscription? You'll keep access until the end of the current period.</div>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmCancel(false)} className="border border-white/20 hover:border-white/50 text-white/70 text-sm px-4 py-2 flex items-center gap-1"><X size={14} /> Keep</button>
                    <button data-testid="confirm-cancel" onClick={cancel} disabled={busy} className="bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide text-sm px-4 py-2">{busy === "cancel" ? "..." : "Confirm Cancel"}</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
